import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TrackByFunction,
  ViewChild,
} from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { each, filter } from 'lodash-es';
import { MatSort, Sort, SortDirection } from '@angular/material/sort';
import { MatTable } from '@angular/material/table';
import { PageEvent } from '@angular/material/paginator';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { NimbusGridGroup } from '@nimbus-library/features/shared/components/nimbus-grid/domain/nimbus-grid-group';
import { NimbusGridDataView } from '../domain/nimbus-grid-data-view';
import { NimbusGridPagerSettings } from '../domain/nimbus-grid-pager-settings';
import { NimbusGridSelectSettings } from '../domain/nimbus-grid-select-settings';
import { NimbusGridSortableSettings } from '../domain/nimbus-grid-sort-settings';
import { NimbusGridColumnComponent } from '../nimbus-grid-column/nimbus-grid-column.component';
import { NimbusGridDetailsTemplateDirective } from '../nimbus-grid-details-template.directive';
import { NimbusGridSort } from '../domain/nimbus-grid-sort';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nimbus-grid',
  templateUrl: './nimbus-grid.component.html',
  styleUrls: ['./nimbus-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NimbusGridComponent implements OnInit, OnChanges, AfterViewInit {
  @ViewChild(MatTable, { static: true, read: ElementRef })
  private matTableRef: ElementRef;

  @HostBinding('class.scrollable')
  @Input()
  public scrollable: boolean = false;

  @HostBinding('class.hide-sort-icons')
  @Input()
  public hideSortIcons: boolean = false;

  @HostBinding('class.hide-header')
  @Input()
  public hideHeader: boolean = false;

  @HostBinding('class')
  public class: string = 'nimbus-grid';

  @ContentChildren(NimbusGridColumnComponent)
  public columns: QueryList<NimbusGridColumnComponent>;

  @ContentChild(NimbusGridDetailsTemplateDirective, { static: true })
  public detailsTemplate: NimbusGridDetailsTemplateDirective;

  @ViewChild(MatSort, { static: true })
  public matSort: MatSort;

  @Input() public pageSize: number;
  @Input() public selectable: NimbusGridSelectSettings = { enabled: false };
  @Input() public rowSelected: (arg: unknown) => boolean;
  @Input() public rowClass: (arg: unknown) => string;
  @Input() public showNoDataRow: boolean = true;
  @Input() public loading: boolean;
  @Input() public skip: number;
  @Input() public data: Array<unknown | NimbusGridGroup> | { data: (unknown | NimbusGridGroup)[]; total: number } = [];
  @Input() public trackBy: TrackByFunction<unknown>;
  @Input() public noRowsPlaceholder: string;
  @Input() public isExpandedEmptyCellDisplayed: boolean = true;

  @Output() public sortingChange: EventEmitter<NimbusGridSort> = new EventEmitter<NimbusGridSort>();
  @Output() public pagingChange: EventEmitter<number> = new EventEmitter<number>();
  @Output() public selectionChange: EventEmitter<unknown[]> = new EventEmitter<unknown[]>();
  @Output() public expandChange: EventEmitter<NimbusGridGroup> = new EventEmitter<NimbusGridGroup>();
  @Output() public scrolled: EventEmitter<Event> = new EventEmitter<Event>();
  @Output() public rowClick: EventEmitter<unknown> = new EventEmitter<unknown>();

  private selection: SelectionModel<unknown> = new SelectionModel(false, []);
  private sortableDefault: NimbusGridSortableSettings = { allowUnsort: false };
  private pageableDefault: NimbusGridPagerSettings = { buttonCount: 10, showFirstLastButtons: true };
  private _sortable: NimbusGridSortableSettings = { ...this.sortableDefault };
  private _pageable: NimbusGridPagerSettings = null;

  public scrollSubject$: Subject<Event> = new Subject<Event>();
  public view: NimbusGridDataView = new NimbusGridDataView([]);
  public pageIndex: number = 0;
  public active: string;
  public direction: SortDirection;

  public readonly icons = ICONS;
  public readonly TranslationKeys = TranslationKeys;

  @Input()
  public set sortable(value: NimbusGridSortableSettings) {
    this._sortable = { ...value, ...this.sortableDefault };
  }

  @Input()
  public set pageable(value: NimbusGridPagerSettings) {
    if (value) {
      this._pageable = { ...this.pageableDefault, ...value };
    } else {
      this._pageable = null;
    }
  }

  @Input()
  public set sort(value: NimbusGridSort[]) {
    this.active = value && value.length > 0 ? value[0].field : undefined;
    this.direction = value && value.length > 0 ? value[0].dir : undefined;
  }

  public get sortable(): NimbusGridSortableSettings {
    return this._sortable;
  }

  public get pageable(): NimbusGridPagerSettings {
    return this._pageable;
  }

  public get columnsDisplayed(): string[] {
    const columns = this.columns.map((x) => x.field);
    return this.detailsTemplate != null ? [...['expandedColumn'], ...columns] : columns;
  }

  public constructor(public wrapper: ElementRef) {}

  @Input() public isRowSelected: (dataItem: unknown) => boolean = () => false;

  public ngOnInit(): void {
    const DEBOUNCE_TIME = 10;

    this.selection.changed.pipe(untilDestroyed(this)).subscribe((x) => this.selectionChange.emit(x.added));
    this.scrollSubject$.pipe(debounceTime(DEBOUNCE_TIME), untilDestroyed(this)).subscribe(($event) => this.scrolled.emit($event));
  }

  public ngOnChanges(e: SimpleChanges): void {
    if (e.data) {
      this.view = new NimbusGridDataView(e.data.currentValue);
      this.initializeSelection();
      this.initializeColumnWidthsWithTimeout();
    }
    if (e.skip || e.pageSize) {
      this.bindPageIndex(this.skip);
    }
  }

  public ngAfterViewInit(): void {
    this.columns.changes.pipe(untilDestroyed(this)).subscribe(() => {
      this.initializeColumnWidthsWithTimeout();
    });
    this.columns.forEach((column) => {
      column.widthChange.pipe(untilDestroyed(this)).subscribe(() => {
        this.setColumnWidth(column);
      });
    });

    this.initializeColumnWidthsWithTimeout();
  }

  public onSortChange(sort: Sort): void {
    const sorting: NimbusGridSort = {
      field: sort.active,
      dir: sort.direction === '' ? null : sort.direction,
    };
    this.sortingChange.emit(sorting);
  }

  public onPageIndexChange(event: PageEvent): void {
    const skip = event.pageIndex * event.pageSize;
    this.pagingChange.emit(skip);
  }

  public onSelect(dataItem: unknown): void {
    this.rowClick.emit(dataItem);
    this.toggleRowSelection(dataItem);
  }

  public isSelected(dataItem: unknown): boolean {
    return this.selectable.enabled ? this.selection.isSelected(dataItem) : false;
  }

  public getRowClass(dataItem: unknown): string {
    if (!this.rowClass) {
      return null;
    }
    return this.rowClass(dataItem);
  }

  public isGroup = (index: number, dataItem: unknown): boolean => dataItem instanceof NimbusGridGroup;

  public isDataRow = (index: number, dataItem: unknown): boolean => !this.isGroup(index, dataItem);

  public onGroupHeaderClick(group: NimbusGridGroup): void {
    group.closed = !group.closed;
    this.expandChange.emit(group);

    const dataSet = {
      total: 0,
      data: [],
    };

    if (Array.isArray(this.data)) {
      dataSet.total = this.data.length;
      dataSet.data = this.data.filter((d) => !group.closed || !group.groupByFn(d));
    } else {
      dataSet.total = this.data.total;
      dataSet.data = this.data.data.filter((d) => !group.closed || !group.groupByFn(d));
    }

    this.view = new NimbusGridDataView(dataSet);
    this.initializeSelection();
  }

  private initializeColumnWidthsWithTimeout(): void {
    setTimeout(() => {
      this.initializeColumnWidths();
    });
  }

  private initializeColumnWidths(): void {
    if (this.columns.length === 0) {
      return;
    }
    each(
      this.columns.filter((x) => !!x.width || x.width === 0),
      (column) => {
        this.setColumnWidth(column);
      },
    );
  }

  private setColumnWidth(column: NimbusGridColumnComponent): void {
    const elements = Array.from(this.matTableRef.nativeElement.getElementsByClassName(`mat-column-${column.field}`));
    elements.forEach((element: HTMLDivElement) => {
      element.style.width = `${column.width}px`;
    });
  }

  private toggleRowSelection(dataItem: unknown): void {
    if (this.selectable.enabled) {
      this.selection.toggle(dataItem);
    }
  }

  private initializeSelection(): void {
    if (!this.view) {
      return;
    }
    if (!this.view.data) {
      return;
    }
    if (!this.rowSelected) {
      return;
    }
    // Restore the visual selection state after a data refresh WITHOUT emitting rowClick.
    // rowClick represents a user interaction; re-emitting it on every data change made
    // selection-driven side effects fire spuriously (e.g. the voicemail detail panel
    // toggled itself shut when a realtime update refreshed the grid).
    const selectedRows = filter(this.view.data, (x) => this.rowSelected(x));
    each(selectedRows, (d) => this.toggleRowSelection(d));
  }

  private bindPageIndex(skip): void {
    if (!skip || !this.pageSize || this.pageSize < 1) {
      this.pageIndex = 0;
      return;
    }
    this.pageIndex = Math.max(0, Math.floor(skip / this.pageSize));
  }
}
